import { useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { getCookieConsent } from '@/components/ui/cookie-banner';
import { t } from '@/lib/translations';

/**
 * SmsFAB — pill design parité cross-Intralys (Buteau / EGSF / Mathis / Serujan).
 *
 * Phase 27.45 — remplace l'ancien WhatsAppFAB. Le href `sms:` ouvre l'app SMS
 * native (iOS Messages / Android Messages). Texte du SMS prérempli identique
 * à l'ancien message WhatsApp (audit AMG gratuit).
 *
 * - Pill horizontal avec icone + nom + telephone visibles sur tous viewports
 * - Tooltip hover desktop au-dessus
 * - Halo sage pulsant 3-layer (rose-blush ajoutee Phase 27.18 combo L.1)
 * - Offset mobile bottom-24 (above MobileStickyBar)
 */
const PHONE_INTL = '14383826496';
const BROKER_NAME = 'Océanne Brodeur';
const PHONE_DISPLAY = '438-382-6496';

export function SmsFAB() {
  const { lang } = useLang();

  // Phase 27.72B — Codex audit fix : hide jusqu'à choix cookies (anti-saturation
  // bas écran mobile 390px). Cookie banner prend la priorité visuelle.
  const [consentDecided, setConsentDecided] = useState<boolean>(
    () => getCookieConsent() !== null
  );
  useEffect(() => {
    if (consentDecided) return;
    const handler = () => setConsentDecided(true);
    window.addEventListener('cookie-consent-change', handler);
    return () => window.removeEventListener('cookie-consent-change', handler);
  }, [consentDecided]);
  if (!consentDecided) return null;

  const message =
    typeof t.mobile.sms_message === 'string'
      ? t.mobile.sms_message
      : t.mobile.sms_message[lang];
  const tooltip =
    typeof t.mobile.sms_tooltip === 'string'
      ? t.mobile.sms_tooltip
      : t.mobile.sms_tooltip[lang];

  // Phase 27.79 a11y — WCAG 2.5.3 (Label in Name) : l'accessible name DOIT
  // commencer par le visible text. Avant : "M'écrire par SMS [name] [phone]"
  // commençait par "M'écrire par SMS" qui n'est pas visible (tooltip hidden
  // sauf hover desktop). Maintenant : visible text + suffix d'intent.
  const ariaLabel =
    lang === 'fr'
      ? `${BROKER_NAME} ${PHONE_DISPLAY} — Écrire par SMS`
      : `${BROKER_NAME} ${PHONE_DISPLAY} — Message by SMS`;

  // sms:+1XXX?&body=... — `?&body=` syntaxe cross-platform (iOS + Android)
  const href = `sms:+${PHONE_INTL}?&body=${encodeURIComponent(message)}`;

  return (
    <a
      href={href}
      aria-label={ariaLabel}
      // Phase 27.60 — déplacé à GAUCHE pour laisser bottom-right au widget GHL Chat.
      // Empilé au-dessus du BackToTop (qui est bottom-6 left-6) avec ~30px gap.
      // Phase 27.72E — DESKTOP ONLY. Sur mobile, MobileStickyBar contient déjà
      // un bouton SMS direct (icône MessageSquare). Le SmsFAB pill avec nom+phone
      // visible était redondant et surchargeait le bas écran mobile.
      className="group hidden md:inline-flex fixed bottom-24 left-6 z-50 items-center gap-3 pl-3 pr-4 py-2.5 rounded-full bg-background border border-secondary/35 hover:border-secondary/70 hover:bg-secondary/10 hover:-translate-y-0.5 transition-all duration-300 shadow-lg"
    >
      {/* Tooltip hover desktop au-dessus du pill — left-0 (au lieu de right-0)
          maintenant que le FAB est sur le côté gauche de l'écran. */}
      <span className="pointer-events-none hidden md:block absolute bottom-full mb-3 left-0 px-3 py-1.5 rounded-md bg-foreground text-background text-xs font-medium whitespace-nowrap opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
        {tooltip}
      </span>

      {/* Halo sage + rose-blush pulsant 3-layer (paused on hover desktop) */}
      <span
        className="absolute inset-0 rounded-full bg-secondary/30 motion-safe:animate-ping pointer-events-none md:group-hover:hidden"
        style={{ animationDuration: '2.4s' }}
        aria-hidden
      />
      <span
        className="absolute inset-0 motion-safe:animate-ping rounded-full bg-rose-blush/30 pointer-events-none md:group-hover:hidden"
        style={{ animationDuration: '4s', animationDelay: '1.2s' }}
        aria-hidden
      />
      <span
        className="absolute -inset-1 rounded-full bg-secondary/15 motion-safe:animate-ping pointer-events-none md:group-hover:hidden"
        style={{ animationDuration: '2.4s', animationDelay: '0.6s' }}
        aria-hidden
      />

      {/* Icone Message dans badge sage circulaire */}
      <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-secondary/15 border border-secondary/40 text-secondary shrink-0">
        <MessageCircle className="w-4 h-4" strokeWidth={2} aria-hidden />
      </span>

      {/* Nom + numero stack vertical */}
      <span className="relative flex flex-col items-start leading-tight whitespace-nowrap">
        <span className="text-sm text-foreground font-bold">{BROKER_NAME}</span>
        <span className="text-[11px] text-foreground/75 font-medium tabular-nums">
          {PHONE_DISPLAY}
        </span>
      </span>
    </a>
  );
}
